import { useState, useEffect } from 'react'
import {
  Loader2,
  AlertCircle,
  CheckCircle,
  X,
  Shield,
  RotateCcw,
  Eye,
} from 'lucide-react'
import { apiGet, apiPost } from '../../services/api'

interface RoleOption {
  value: string
  label: string
}

interface ModuleInfo {
  label: string
  section: string
  actions: string[]
}

interface ScopeModuleInfo {
  label: string
  default_scopes: Record<string, string>
}

interface PermissionConfig {
  id: number
  company_id: number
  role: string
  permissions: Record<string, Record<string, boolean>>
  data_scope?: Record<string, string> | null
  is_active: boolean
}

interface RolePermissionsModalProps {
  isOpen: boolean
  onClose: () => void
  companyId: number
  companyName: string
}

const ACTION_LABELS: Record<string, string> = {
  view: 'View',
  create: 'Create',
  edit: 'Edit',
  delete: 'Delete',
  export: 'Export',
  sync: 'Sync',
  check_in: 'Check In',
  check_out: 'Check Out',
  cancel: 'Cancel',
  submit: 'Submit',
  start: 'Start',
  pause: 'Pause',
  stop: 'Stop',
  approve: 'Approve',
  answer: 'Answer',
}

const SCOPE_LABELS: Record<string, string> = {
  all: 'All (Company-wide)',
  department: 'Department + Children',
  team: 'Direct Reports Only',
  self: 'Own Data Only',
}

export default function RolePermissionsModal({ isOpen, onClose, companyId, companyName }: RolePermissionsModalProps) {
  const [roles, setRoles] = useState<RoleOption[]>([])
  const [modules, setModules] = useState<Record<string, ModuleInfo>>({})
  const [configs, setConfigs] = useState<PermissionConfig[]>([])
  const [defaults, setDefaults] = useState<Record<string, Record<string, Record<string, boolean>>>>({})
  const [activeRole, setActiveRole] = useState<string>('')
  const [permissions, setPermissions] = useState<Record<string, Record<string, boolean>>>({})
  const [scopeModules, setScopeModules] = useState<Record<string, ScopeModuleInfo>>({})
  const [scopeOptions, setScopeOptions] = useState<string[]>([])
  const [defaultScopes, setDefaultScopes] = useState<Record<string, Record<string, string>>>({})
  const [dataScope, setDataScope] = useState<Record<string, string>>({})
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      fetchData()
    }
  }, [isOpen, companyId])

  const fetchData = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const [permRes, modulesRes, evOptionsRes] = await Promise.all([
        apiGet<{
          data: PermissionConfig[]
          defaults: Record<string, Record<string, Record<string, boolean>>>
        }>(`/role-permissions?company_id=${companyId}`),
        apiGet<{
          modules: Record<string, ModuleInfo>
          scope_modules: Record<string, ScopeModuleInfo>
          scope_options: string[]
          default_scopes: Record<string, Record<string, string>>
        }>('/role-permissions/modules'),
        apiGet<{ ev_roles: RoleOption[] }>(`/role-mappings/ev-options?company_id=${companyId}`),
      ])

      setConfigs(permRes.data)
      setDefaults(permRes.defaults)
      const availableModules = {
        ...modulesRes.modules,
        admin_learning_analytics: modulesRes.modules.admin_learning_analytics || {
          label: 'Learning Analytics',
          section: 'admin',
          actions: ['view'],
        },
      }
      setModules(availableModules)
      setScopeModules(modulesRes.scope_modules || {})
      setScopeOptions(modulesRes.scope_options || [])
      setDefaultScopes(modulesRes.default_scopes || {})

      const fetchedRoles = evOptionsRes.ev_roles?.length
        ? evOptionsRes.ev_roles
        : [
            { value: 'company_admin', label: 'Company Admin' },
            { value: 'supervisor', label: 'Supervisor' },
          ]
      setRoles(fetchedRoles)

      // Set first designation as active
      const firstRole = fetchedRoles[0]?.value || ''
      setActiveRole(firstRole)
      loadPermissionsForRole(firstRole, permRes.data, permRes.defaults, modulesRes.default_scopes, availableModules)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load permissions')
    } finally {
      setIsLoading(false)
    }
  }

  const loadPermissionsForRole = (
    role: string,
    configsData?: PermissionConfig[],
    defaultsData?: Record<string, Record<string, Record<string, boolean>>>,
    defaultScopesData?: Record<string, Record<string, string>>,
    modulesData?: Record<string, ModuleInfo>
  ) => {
    const cfgs = configsData || configs
    const defs = defaultsData || defaults
    const scopeDefs = defaultScopesData || defaultScopes
    const mods = modulesData || modules

    const existing = cfgs.find((c) => c.role === role)
    const base = defs[role] || defs['_fallback'] || {}

    if (existing && existing.permissions) {
      // Merge existing config: unconfigured modules default to all OFF
      const merged: Record<string, Record<string, boolean>> = {}
      for (const [key, mod] of Object.entries(mods)) {
        if (key in existing.permissions) {
          merged[key] = existing.permissions[key]
        } else {
          // New module not yet in saved config - show as all unchecked
          merged[key] = Object.fromEntries(mod.actions.map((a: string) => [a, false]))
        }
      }
      if (!('admin_learning_analytics' in merged)) {
        merged.admin_learning_analytics = { view: false }
      }
      setPermissions(merged)
    } else if (Object.keys(base).length > 0) {
      setPermissions({
        ...base,
        admin_learning_analytics: base.admin_learning_analytics || {
          view: role === 'company_admin',
        },
      })
    } else {
      // Build empty permissions from modules (all unchecked)
      const empty: Record<string, Record<string, boolean>> = {}
      for (const [key, mod] of Object.entries(mods)) {
        empty[key] = {}
        for (const action of mod.actions) {
          empty[key][action] = false
        }
      }
      empty.admin_learning_analytics = { view: role === 'company_admin' }
      setPermissions(empty)
    }

    // Load data scope
    if (existing && existing.data_scope) {
      setDataScope(existing.data_scope)
    } else {
      setDataScope(scopeDefs[role] || scopeDefs['_fallback'] || {})
    }
  }

  const handleRoleTabClick = (role: string) => {
    setActiveRole(role)
    loadPermissionsForRole(role)
    setError(null)
    setSuccess(null)
  }

  const handleToggle = (moduleKey: string, action: string) => {
    setPermissions((prev) => {
      const modulePerms = { ...(prev[moduleKey] || {}) }

      if (action === 'view' && modulePerms['view']) {
        // Unchecking view disables all actions for this module
        const disabled: Record<string, boolean> = {}
        for (const a of Object.keys(modulePerms)) {
          disabled[a] = false
        }
        return { ...prev, [moduleKey]: disabled }
      }

      if (action !== 'view' && !modulePerms['view']) {
        // Cannot enable sub-actions without view
        return prev
      }

      modulePerms[action] = !modulePerms[action]
      return { ...prev, [moduleKey]: modulePerms }
    })
  }

  const handleResetToDefaults = () => {
    const defaultPerms = defaults[activeRole] || defaults['_fallback'] || {}
    setPermissions(defaultPerms)
    const defaultScopeForRole = defaultScopes[activeRole] || defaultScopes['_fallback'] || {}
    setDataScope(defaultScopeForRole)
  }

  const handleSave = async () => {
    setIsSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const res = await apiPost<{ data: PermissionConfig }>('/role-permissions', {
        company_id: companyId,
        role: activeRole,
        permissions,
        data_scope: dataScope,
      })

      // Update local configs
      setConfigs((prev) => {
        const idx = prev.findIndex((c) => c.role === activeRole)
        if (idx >= 0) {
          const updated = [...prev]
          updated[idx] = res.data
          return updated
        }
        return [...prev, res.data]
      })

      setSuccess('Permissions saved successfully')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save permissions')
    } finally {
      setIsSaving(false)
    }
  }

  const isCustom = (role: string) => {
    return configs.some((c) => c.role === role)
  }

  const adminModules = Object.entries(modules).filter(([, m]) => m.section === 'admin')
  const pwaModules = Object.entries(modules).filter(([, mod]) => mod.section === 'pwa')

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between bg-gray-50">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-indigo-600" />
            <div>
              <h2 className="text-lg font-semibold">Designation Permissions</h2>
              <p className="text-sm text-gray-500">{companyName}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Messages */}
          {error && (
            <div className="mb-4 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
              <button onClick={() => setError(null)} className="ml-auto">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
          {success && (
            <div className="mb-4 flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              <CheckCircle className="h-4 w-4 flex-shrink-0" />
              <span>{success}</span>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
            </div>
          ) : (
            <>
              {/* Designation Tabs */}
              <div className="flex flex-wrap gap-2 mb-6">
                {roles.map((role) => (
                  <button
                    key={role.value}
                    onClick={() => handleRoleTabClick(role.value)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                      activeRole === role.value
                        ? 'bg-indigo-100 text-indigo-700 border border-indigo-300'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-transparent'
                    }`}
                  >
                    {role.label}
                    {isCustom(role.value) ? (
                      <span className="px-1.5 py-0.5 text-xs rounded bg-indigo-200 text-indigo-800">Custom</span>
                    ) : (
                      <span className="px-1.5 py-0.5 text-xs rounded bg-gray-200 text-gray-600">Default</span>
                    )}
                  </button>
                ))}
              </div>

              {/* Permission Grid */}
              <div className="space-y-6">
                {/* Admin Pages Section */}
                {adminModules.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                      Admin Pages
                    </h3>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 border-b">
                            <th className="text-left py-2 px-3 font-medium text-gray-700">Module</th>
                            {['view', 'create', 'edit', 'delete', 'export', 'sync'].map((action) => (
                              <th key={action} className="text-center py-2 px-2 font-medium text-gray-700 w-16">
                                {ACTION_LABELS[action] || action}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {adminModules.map(([key, mod]) => {
                            const modulePerms = permissions[key] || {}
                            return (
                              <tr key={key} className="border-b last:border-b-0 hover:bg-gray-50">
                                <td className="py-2 px-3 font-medium text-gray-800">{mod.label}</td>
                                {['view', 'create', 'edit', 'delete', 'export', 'sync'].map((action) => (
                                  <td key={action} className="text-center py-2 px-2">
                                    {mod.actions.includes(action) ? (
                                      <input
                                        type="checkbox"
                                        checked={!!modulePerms[action]}
                                        onChange={() => handleToggle(key, action)}
                                        disabled={action !== 'view' && !modulePerms['view']}
                                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-30"
                                      />
                                    ) : (
                                      <span className="text-gray-300">-</span>
                                    )}
                                  </td>
                                ))}
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* PWA/User Pages Section */}
                {pwaModules.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                      User / PWA Pages
                    </h3>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 border-b">
                            <th className="text-left py-2 px-3 font-medium text-gray-700">Module</th>
                            {['view', 'create', 'edit', 'delete', 'submit', 'check_in', 'check_out', 'cancel', 'approve', 'answer', 'start', 'pause', 'stop'].map((action) => (
                              <th key={action} className="text-center py-2 px-2 font-medium text-gray-700 w-16">
                                {ACTION_LABELS[action] || action}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {pwaModules.map(([key, mod]) => {
                            const modulePerms = permissions[key] || {}
                            return (
                              <tr key={key} className="border-b last:border-b-0 hover:bg-gray-50">
                                <td className="py-2 px-3 font-medium text-gray-800">{mod.label}</td>
                                {['view', 'create', 'edit', 'delete', 'submit', 'check_in', 'check_out', 'cancel', 'approve', 'answer', 'start', 'pause', 'stop'].map((action) => (
                                  <td key={action} className="text-center py-2 px-2">
                                    {mod.actions.includes(action) ? (
                                      <input
                                        type="checkbox"
                                        checked={!!modulePerms[action]}
                                        onChange={() => handleToggle(key, action)}
                                        disabled={action !== 'view' && !modulePerms['view']}
                                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-30"
                                      />
                                    ) : (
                                      <span className="text-gray-300">-</span>
                                    )}
                                  </td>
                                ))}
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Data Visibility Scope Section */}
                {Object.keys(scopeModules).length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Eye className="h-4 w-4" />
                      Data Visibility Scope
                    </h3>
                    <p className="text-xs text-gray-500 mb-3">
                      Controls which records this designation can see when they have access to HR modules.
                    </p>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 border-b">
                            <th className="text-left py-2 px-3 font-medium text-gray-700">Data Module</th>
                            <th className="text-left py-2 px-3 font-medium text-gray-700">Scope Level</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(scopeModules).map(([key, mod]) => (
                            <tr key={key} className="border-b last:border-b-0 hover:bg-gray-50">
                              <td className="py-2 px-3 font-medium text-gray-800">{mod.label}</td>
                              <td className="py-2 px-3">
                                <select
                                  value={dataScope[key] || 'self'}
                                  onChange={(e) =>
                                    setDataScope((prev) => ({ ...prev, [key]: e.target.value }))
                                  }
                                  className="block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                >
                                  {scopeOptions.map((opt) => (
                                    <option key={opt} value={opt}>
                                      {SCOPE_LABELS[opt] || opt}
                                    </option>
                                  ))}
                                </select>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {!isLoading && (
          <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
            <button
              onClick={handleResetToDefaults}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <RotateCcw className="h-4 w-4" />
              Reset to Defaults
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="btn-primary flex items-center gap-2"
            >
              {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
              Save Permissions
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
